/* eslint-disable import/max-dependencies */
/* eslint-disable max-lines */
import _ from 'lodash';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useGetUserRoles, useUserRoleUpdate } from 'src/hooks/getRoles';
import Paper from '@mui/material/Paper';
import { useTranslate } from '@im/react-localization';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import { StyledText } from 'src/components/StyledTypography';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  useUserRole,
  useDisplayUserRoles,
} from 'src/provider/UserRoleProvider';
import useSnackBar from 'src/hooks/useSnackBar';
import { LoadingButton } from '@mui/lab';
import { CircularLoader } from '@im/ui-lib/CircularLoader';
import { useAccount } from '@azure/msal-react';
import { useIsEnvironment } from 'src/hooks/useIsEnvironment';
import { useCountries } from 'src/util/countries';

export default function UsersDashboard(params: any) {
  const translate = useTranslate();
  const { showSnackBar } = useSnackBar();
  const cuurentUserId: string = useAccount()?.username ?? '';
  const currentUserRole = useUserRole();
  const environment = useIsEnvironment();
  const envName = Object.entries(environment).find(
    ([key, value]) => value
  )?.[0];

  const formattedEnvName = envName
    ? envName.replace(/^is(.*)Environment$/, '$1').toUpperCase()
    : 'Unknown';

  const countryDropdown = _.orderBy(useCountries(), ['label', ['asc']]);
  const userRoles = useDisplayUserRoles();

  const [formData, setFormData] = useState<{
    defaultCountryCode: string;
    isAdmin: boolean;
    userId: string;
    userRole: number;
  }>({
    defaultCountryCode: '',
    isAdmin: false,
    userId: '',
    userRole: 100,
  });
  const [formError, setFormError] = useState<{
    userId: string;
  }>({
    userId: '',
  });
  const [countryList, setCountryList] = useState<string[]>(
    currentUserRole.listOfAccessableCountries.map((c) =>
      c.value.split('-')[0].toLowerCase()
    )
  );
  const [userId, setUserId] = useState('');

  const GetUserRolesByUserId = useGetUserRoles({ Usermailid: userId });

  const isExistingUser =
    GetUserRolesByUserId.data?.defaultCountryCompanyCode !== null &&
    GetUserRolesByUserId.data?.listOfAccessableCountries !== null;
  const updateRole = useUserRoleUpdate();
  const handleClear = () => {
    setFormData({
      defaultCountryCode: '',
      isAdmin: false,
      userId: '',
      userRole: 100,
    });
    setFormError({ userId: '' });
    setCountryList([]);
  };

  const handleSelectAllCountries = (
    event: React.SyntheticEvent<Element, Event>,
    checked: boolean
  ) => {
    setCountryList(checked ? countryDropdown.map((v) => v.value) : []);
  };

  const onSubmitClick = () => {
    updateRole
      .mutateAsync({
        requestPreamble: {
          adminRole: formData.isAdmin ? 120 : 0,
          countryMapping:
            countryList.length === 0
              ? formData.defaultCountryCode
              : countryList.toString(),
          defaultCountryCode: formData.defaultCountryCode,
          eventType: isExistingUser ? 'update' : 'insert',
          requestNumber: `REQ${formattedEnvName}UI`,
          userId: formData.userId.toLowerCase(),
          userRole: formData.userRole,
        },
      })
      .then((res) => {
        if (res.statusCode === 200)
          showSnackBar({
            autoHide: 5000,
            message: res.responsePreamble.transactionStatus,
            severity: 'success',
          });
        else
          showSnackBar({
            autoHide: 5000,
            message: res.responsePreamble.transactionStatus,
            severity: 'info',
          });
        cuurentUserId.toLowerCase() ===
          res.responsePreamble.transactionID?.toLowerCase() &&
          window.location.reload();
      })
      .catch(
        (err) =>
          err.response?.responsePreamble?.errors?.[0] &&
          showSnackBar({
            autoHide: 5000,
            message: err.response.responsePreamble.errors[0].errorMessage,
            severity: 'error',
          })
      );
  };

  const onChange = (field: string, value: string | boolean) => {
    if (field === 'userId') {
      setFormError({
        userId: !RegExp(/^[a-z0-9._%+-]+@ingrammicro.com$/).exec(
          value as string
        )
          ? translate('UserRoles.MessageValidEmailId')
          : '',
      });
    }
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  useEffect(() => {
    if (isExistingUser) {
      setFormData({
        ...formData,
        defaultCountryCode:
          GetUserRolesByUserId.data?.defaultCountryCompanyCode
            .split('-')[0]
            .toLowerCase() ??
          currentUserRole.defaultCountryCompanyCode.split('-')[0].toLowerCase(),
        isAdmin: GetUserRolesByUserId.data?.role.includes(120) ?? false,
        userRole: GetUserRolesByUserId.data?.role.filter(
          (r) => r !== 120
        )[0] as number,
      });
    }
    setCountryList(
      GetUserRolesByUserId.data?.listOfAccessableCountries
        ? GetUserRolesByUserId.data?.listOfAccessableCountries.map((c) =>
            c.value.split('-')[0].toLowerCase()
          )
        : []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GetUserRolesByUserId.dataUpdatedAt]);

  return (
    <Box p={4}>
      <Stack direction="row" justifyContent="center" width="100%">
        <Grid container item spacing={2} xs={6}>
          <Grid item xs={12}>
            <Paper variant="elevation">
              <form style={{ padding: '30px' }}>
                <Typography
                  data-testid="gds-users-heading"
                  sx={{ fontSize: '24px', fontWeight: 600 }}
                >
                  {translate('UserRoles.Heading')}
                </Typography>
                <Stack
                  gap={4}
                  mt={4}
                  sx={{
                    '.MuiInputLabel-root': {
                      fontSize: '22px !important',
                    },
                  }}
                >
                  <FormControl>
                    <TextField
                      InputProps={{
                        ...(GetUserRolesByUserId.isLoading && {
                          endAdornment: <CircularLoader size={20} />,
                        }),
                      }}
                      data-testid="gds-users-user-id-field"
                      error={formError.userId !== ''}
                      helperText={formError.userId}
                      label={`${translate('UserRoles.UserId')}`}
                      name="User ID"
                      onBlur={() =>
                        formError.userId === '' && setUserId(formData.userId)
                      }
                      onChange={(event) =>
                        onChange('userId', event.target.value)
                      }
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        formError.userId === '' &&
                        setUserId(formData.userId)
                      }
                      placeholder={translate('Common.EmailPlaceholder')}
                      required
                      sx={{
                        '.MuiCircularProgress-root': {
                          verticalAlign: 'middle',
                        },
                      }}
                      value={formData.userId}
                    />
                  </FormControl>
                  <FormControl>
                    <TextField
                      data-testid="gds-users-default-country-field"
                      disabled={isExistingUser}
                      label={translate('UserRoles.DefaultCountryCode')}
                      name="Default country access"
                      onChange={(event) =>
                        onChange('defaultCountryCode', event.target.value)
                      }
                      required
                      select
                      value={formData.defaultCountryCode}
                    >
                      {countryDropdown.map((country) => (
                        <MenuItem key={country.value} value={country.value}>
                          <StyledText>{country.label}</StyledText>
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                  <FormControl>
                    <TextField
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            style: {
                              maxHeight: '250px',
                            },
                          },
                        },
                        multiple: true,
                        onChange: (event: any) =>
                          setCountryList(event.target.value),
                        renderValue: (selected: any) =>
                          selected
                            .map(
                              (valu: string) =>
                                countryDropdown.filter(
                                  (country) => country.value === valu
                                )[0]?.label
                            )
                            .sort()
                            .join(', '),
                      }}
                      data-testid="gds-users-country-mapping-field"
                      label={translate('UserRoles.CountryMapping')}
                      name="Required country access"
                      required
                      select
                      value={countryList}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              countryList.length === countryDropdown.length
                            }
                          />
                        }
                        label={
                          <StyledText>
                            {translate('Common.SelectAll')}
                          </StyledText>
                        }
                        onChange={handleSelectAllCountries}
                        sx={{ margin: 0, width: '100%' }}
                      />
                      {countryDropdown.map((country) => (
                        <MenuItem key={country.value} value={country.value}>
                          <Checkbox
                            checked={countryList.indexOf(country.value) > -1}
                          />
                          <StyledText>{country.label}</StyledText>
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                  <FormControl>
                    <TextField
                      data-testid="gds-users-user-role-field"
                      label={translate('UserRoles.UserRole')}
                      name="User role"
                      onChange={(event: any) =>
                        onChange('userRole', event.target.value)
                      }
                      required
                      select
                      value={formData.userRole}
                    >
                      {userRoles.map((role) => (
                        <MenuItem key={role.value} value={role.value}>
                          <StyledText>{role.label}</StyledText>
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>

                  {/*  <FormControlLabel
                    control={<Checkbox checked={formData.isAdmin} />}
                    label={translate('Common.Admin')}
                    onChange={(_, checked) => onChange('isAdmin', checked)}
                    sx={{
                      color: '#6B6B6B',
                    }}
                  /> */}
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  mt={2}
                  spacing={1}
                >
                  <Button
                    data-testid="gds-users-clear-form"
                    onClick={handleClear}
                    variant="newOutlined"
                  >
                    {translate('Common.Clear')}
                  </Button>
                  <LoadingButton
                    data-testid="gds-users-submit-form"
                    disabled={
                      !(
                        formData.userId &&
                        formData.defaultCountryCode &&
                        formData.userRole &&
                        countryList.length > 0
                      )
                    }
                    loading={updateRole.isLoading}
                    onClick={onSubmitClick}
                    variant="newContained"
                  >
                    {translate('Common.Submit')}
                  </LoadingButton>
                </Stack>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
